import { useEffect, useState, useMemo } from 'react'

import { Divider, Loader, TextInput } from '@mantine/core'

import { useAuth } from 'src/auth'
import { useMutation, useQuery } from '@redwoodjs/web'

import CallTable from 'src/components/ActivityLogs/CallLog/CallTable'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import { GET_VOICE_AGENTS } from 'src/graphql/voiceAgents.graphql'
import { GET_VOICEMAIL_TEMPLATES } from 'src/graphql/voiceMailTemplate.graphql'
import { GET_EMPLOYEE_PERMISSIONS } from 'src/graphql/permissions.graphql.tsx'
import { GET_EMPLOYEES } from 'src/graphql/employees.graphql'
import IconSearch from 'src/icons/IconSearch'
import IconCheckmark from 'src/icons/IconCheckmark'
import { formatSnakeValue } from 'src/lib/formatters'
import ActionsMenu from 'src/components/Overlays/ActionsMenu/ActionsMenu'
import { DOWNLOAD_CALL_CSV } from 'src/graphql/activityLogs.graphql'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { base64ToBlob, downloadFileBlob } from 'src/lib/string-util'

import DateRangeMenu from '../../../components/Overlays/DateRangeMenu'
import {
  callTypeOptions,
  getUrlParams,
  serializeDateRangeForUrl,
  updateUrl,
} from '../../CallAnalyticsPage/utils'

import Filters from './Filters'

const CallHistoryTab = ({ onOpen, setTotalCalls }) => {
  const { currentUser } = useAuth()
  const urlParams = getUrlParams()
  const [searchText, setSearchText] = useState('')
  const [startDate, setStartDate] = useState(urlParams.defaultStartDate)
  const [endDate, setEndDate] = useState(urlParams.defaultEndDate)
  const [selectedConnectedStatus, setSelectedConnectedStatus] = useState(
    urlParams.defaultConnectedStatus
  )
  const [callDirections, setCallDirections] = useState(
    urlParams.defaultCallDirections
  )
  const [callTypes, setCallTypes] = useState(urlParams.defaultCallTypes)
  const [callStatuses, setCallStatuses] = useState(
    urlParams.defaultCallStatuses
  )
  const [
    selectedVoiceAgentAnalysisResults,
    setSelectedVoiceAgentAnalysisResults,
  ] = useState(urlParams.defaultVoiceAgentAnalysisResults)
  const [selectedVoiceAgents, setSelectedVoiceAgents] = useState(
    urlParams.defaultVoiceAgents
  )
  const [selectedCallTags, setSelectedCallTags] = useState<string[]>(
    urlParams.defaultCallTags ?? []
  )
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>(
    urlParams.defaultEmployees ?? []
  )
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [
    searchText,
    startDate,
    endDate,
    callDirections,
    callTypes,
    callStatuses,
    selectedConnectedStatus,
    selectedVoiceAgentAnalysisResults,
    selectedVoiceAgents,
    selectedCallTags,
    selectedEmployees,
  ])

  const { data: voiceAgentsData, loading: voiceAgentsLoading } = useQuery(
    GET_VOICE_AGENTS,
    {
      variables: {
        pageInfo: {
          limit: 100,
          offset: 0,
        },
      },
    }
  )

  // Load employee permissions for permission-based UI
  const { data: permissionsData } = useQuery(GET_EMPLOYEE_PERMISSIONS, {
    variables: { employeeId: currentUser?.employeeId },
    skip: !currentUser?.employeeId,
  })

  // Load employees for filter
  const { data: employeesData } = useQuery(GET_EMPLOYEES, {
    variables: {
      pageInfo: {
        limit: 100,
        offset: 0,
      },
    },
  })

  const hasExportPermission = useMemo(() => {
    if (!permissionsData?.employeePermissions) return false
    return permissionsData.employeePermissions.some(
      (perm) =>
        perm.permissionKey === 'export:applicant_table_csv' && !perm.revokedAt
    )
  }, [permissionsData])

  const canDownloadCSV =
    currentUser?.roles?.includes('ADMIN') ||
    currentUser?.roles?.includes('RECRUITER') ||
    hasExportPermission

  const voiceAgentOptions =
    voiceAgentsData?.voiceAgents?.items?.map((agent) => ({
      label: agent.name,
      value: agent.voiceAgentId,
    })) || []

  const { data: voiceMailTemplatesData } = useQuery(GET_VOICEMAIL_TEMPLATES, {
    variables: { pageInfo: { limit: 100, offset: 0 }, onlyUsed: true },
  })

  // Graceful degradation: empty array while loading or on error
  const voiceMailTemplateOptions =
    voiceMailTemplatesData?.voiceMailTemplates?.items?.map((t) => ({
      label: t.title,
      value: t.voiceMailTemplateId,
    })) ?? []

  const employeeOptions =
    employeesData?.employees?.items?.map((emp) => ({
      label: `${emp.firstName} ${emp.lastName}`,
      value: emp.employeeId,
    })) || []

  const buildFilters = () => {
    const filters: Record<string, unknown> = {}

    if (searchText) {
      filters.searchText = searchText
    }
    if (callDirections.length) {
      filters.callDirections = [...callDirections]
    }
    if (callTypes.length) {
      filters.callTypes = [...callTypes]
    }
    if (callStatuses.length) {
      filters.callStatuses = [...callStatuses]
    }
    if (selectedConnectedStatus.length) {
      filters.connectedStatuses = [...selectedConnectedStatus]
    }
    if (selectedVoiceAgentAnalysisResults.length) {
      filters.analysisResults = [...selectedVoiceAgentAnalysisResults]
    }
    if (selectedVoiceAgents.length) {
      filters.voiceAgents = [...selectedVoiceAgents]
    }
    const voeTag = selectedCallTags.includes('voe')
    const templateIds = selectedCallTags.filter((t) => t !== 'voe')
    if (voeTag) filters.hasVoe = true
    if (templateIds.length > 0) filters.voiceMailTemplateIds = templateIds
    if (startDate && endDate) {
      filters.createdAt = { gte: startDate, lte: endDate }
    }
    if (selectedEmployees.length) {
      filters.employeeIds = [...selectedEmployees]
    }

    return filters
  }

  const [downloadCallCSV] = useMutation(DOWNLOAD_CALL_CSV, {
    onCompleted: (result) => {
      if (result?.downloadCallCSV?.fileContent) {
        const fileAsBinary = base64ToBlob({
          base64String: result.downloadCallCSV.fileContent,
          contentType: 'text/csv',
        })
        downloadFileBlob({
          file: fileAsBinary,
          fileName: result.downloadCallCSV.fileName,
        })
      }
    },
    onError: (error) => {
      toast(error.message, 'error')
    },
  })

  const handleDownloadCSV = () => {
    downloadCallCSV({ variables: { filters: buildFilters() } })
  }

  return (
    <>
      <div className="my-4 flex w-full flex-row gap-2 overflow-y-hidden">
        <div className="flex flex-1 flex-row gap-2 overflow-auto">
          {callDirections.map((direction) => (
            <Tag
              key={direction}
              onClose={() => {
                const newDirections = callDirections.filter(
                  (item) => item !== direction
                )
                updateUrl('callDirections', newDirections.join(','))
                setCallDirections(newDirections)
              }}
            >
              {formatSnakeValue(direction)}
            </Tag>
          ))}
          {callTypes.map((type) => (
            <Tag
              key={type}
              onClose={() => {
                const newCallTypes = callTypes.filter((item) => item !== type)
                updateUrl('callTypes', newCallTypes.join(','))
                setCallTypes(newCallTypes)
              }}
            >
              {callTypeOptions.find((option) => option.value === type).label}
            </Tag>
          ))}
          {callStatuses.map((status) => (
            <Tag
              key={status}
              onClose={() => {
                const newCallStatuses = callStatuses.filter(
                  (item) => item !== status
                )
                updateUrl('callStatuses', newCallStatuses.join(','))
                setCallStatuses(newCallStatuses)
              }}
            >
              {formatSnakeValue(status)}
            </Tag>
          ))}
          {selectedConnectedStatus.map((status) => (
            <Tag
              key={status}
              onClose={() => {
                const newConnectedStatuses = selectedConnectedStatus.filter(
                  (item) => item !== status
                )
                updateUrl('connectedStatus', newConnectedStatuses.join(','))
                setSelectedConnectedStatus(newConnectedStatuses)
              }}
            >
              {formatSnakeValue(status)}
            </Tag>
          ))}
          {selectedVoiceAgentAnalysisResults.map((result) => (
            <Tag
              key={result}
              onClose={() => {
                setSelectedVoiceAgentAnalysisResults(
                  selectedVoiceAgentAnalysisResults.filter((s) => s !== result)
                )
                updateUrl(
                  'analysisResults',
                  selectedVoiceAgentAnalysisResults
                    .filter((s) => s !== result)
                    .join(',')
                )
              }}
            >
              {formatSnakeValue(result)}
            </Tag>
          ))}
          {selectedVoiceAgents.map((voiceAgentId) => {
            const voiceAgent = voiceAgentOptions.find(
              (option) => option.value === voiceAgentId
            )
            return (
              <Tag
                key={voiceAgentId}
                onClose={() => {
                  setSelectedVoiceAgents(
                    selectedVoiceAgents.filter((s) => s !== voiceAgentId)
                  )
                  updateUrl(
                    'voiceAgents',
                    selectedVoiceAgents
                      .filter((s) => s !== voiceAgentId)
                      .join(',')
                  )
                }}
              >
                {voiceAgentsLoading ? (
                  <Loader size={'xs'} color="gray" />
                ) : (
                  voiceAgent?.label || voiceAgentId
                )}
              </Tag>
            )
          })}
          {selectedCallTags.map((tag) => {
            const label =
              tag === 'voe'
                ? 'VoE Call'
                : voiceMailTemplateOptions.find((t) => t.value === tag)?.label ??
                  tag
            return (
              <Tag
                key={tag}
                onClose={() => {
                  const next = selectedCallTags.filter((t) => t !== tag)
                  setSelectedCallTags(next)
                  updateUrl('callTags', next.join(','))
                }}
              >
                {label}
              </Tag>
            )
          })}
          {selectedEmployees.map((employeeId) => {
            const employee = employeeOptions.find(
              (option) => option.value === employeeId
            )
            return (
              <Tag
                key={employeeId}
                onClose={() => {
                  const newEmployees = selectedEmployees.filter(
                    (item) => item !== employeeId
                  )
                  updateUrl('employees', newEmployees.join(','))
                  setSelectedEmployees(newEmployees)
                }}
              >
                {employee?.label || employeeId}
              </Tag>
            )
          })}
        </div>
        {currentUser?.employeeId && (
          <div className="flex flex-row items-center gap-2">
            <div className="text-xs font-medium text-doubleNickel-gray-700">
              Suggested: {/* // pragma: allowlist secret */}
            </div>
            <Tag
              clickable={true}
              onClick={() => {
                if (selectedEmployees.includes(currentUser.employeeId)) {
                  const newEmployees = selectedEmployees.filter(
                    (id) => id !== currentUser.employeeId
                  )
                  setSelectedEmployees(newEmployees)
                  updateUrl('employees', newEmployees.join(','))
                } else {
                  const newEmployees = [
                    ...selectedEmployees,
                    currentUser.employeeId,
                  ]
                  setSelectedEmployees(newEmployees)
                  updateUrl('employees', newEmployees.join(','))
                }
              }}
              showCloseButton={false}
            >
              {selectedEmployees.includes(currentUser.employeeId) && (
                <IconCheckmark className="h-3 w-3 fill-none stroke-doubleNickel-brand-400" />
              )}
              {currentUser.firstName} {currentUser.lastName}
            </Tag>
          </div>
        )}
        <TextInput
          className="w-[300px]"
          placeholder="Search calls by name or phone number"
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
          leftSection={
            <IconSearch className="h-3 w-3 fill-none stroke-doubleNickel-gray-700" />
          }
        />
        <Filters
          callDirections={callDirections}
          setCallDirections={setCallDirections}
          callTypes={callTypes}
          setCallTypes={setCallTypes}
          callStatuses={callStatuses}
          setCallStatuses={setCallStatuses}
          selectedConnectedStatus={selectedConnectedStatus}
          setSelectedConnectedStatus={setSelectedConnectedStatus}
          selectedVoiceAgentAnalysisResults={selectedVoiceAgentAnalysisResults}
          setSelectedVoiceAgentAnalysisResults={
            setSelectedVoiceAgentAnalysisResults
          }
          selectedVoiceAgents={selectedVoiceAgents}
          setSelectedVoiceAgents={setSelectedVoiceAgents}
          selectedCallTags={selectedCallTags}
          setSelectedCallTags={setSelectedCallTags}
          voiceMailTemplateOptions={voiceMailTemplateOptions}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
          employeeOptions={employeeOptions}
        />
        <DateRangeMenu
          startDate={startDate}
          endDate={endDate}
          clearable={false}
          handleChange={(value) => {
            setStartDate(value[0])
            setEndDate(value[1])
            updateUrl('dateRange', serializeDateRangeForUrl(value[0], value[1]))
          }}
        />
        <ActionsMenu
          text="Actions"
          items={[
            {
              label: 'Download CSV',
              onClick: handleDownloadCSV,
              disabled: !canDownloadCSV,
            },
          ]}
        />
      </div>

      <div className="flex-1 overflow-hidden">
        <CallTable
          connectedStatuses={selectedConnectedStatus}
          searchText={searchText}
          callDirections={callDirections}
          page={page}
          setTotalPages={setTotalPages}
          setTotalCalls={setTotalCalls}
          callStatuses={callStatuses}
          callTypes={callTypes}
          analysisResults={selectedVoiceAgentAnalysisResults}
          createdAt={{
            gte: startDate,
            lte: endDate,
          }}
          onOpen={onOpen}
          voiceAgents={selectedVoiceAgents}
          hasVoe={selectedCallTags.includes('voe') ? true : null}
          voiceMailTemplateIds={
            selectedCallTags.filter((t) => t !== 'voe').length > 0
              ? selectedCallTags.filter((t) => t !== 'voe')
              : null
          }
          employeeIds={selectedEmployees.length > 0 ? selectedEmployees : null}
        />
      </div>
      <PaginationArea
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </>
  )
}

export default CallHistoryTab
